<template>
  <div class="w-full flex justify-between gap-2">
    <MenuItem as="button" :on-click="redirectToCanva" :disabled="isLoading">
      <template #start>
        <component :is="iconComponent" />
      </template>
      <template #title>
        <span class="text-typo-primary font-semi-bold text-sm truncate">{{ props.label }}</span>
      </template>
      <template #end>
        <Badge v-if="shouldShowLoading" tone="secondary" class="flex items-center !p-1">
          <div class="loading-animation-container">
            <Spinner />
          </div>
        </Badge>
        <Badge v-else tone="secondary" class="flex items-center !p-1">
          <CanvaIcon width="16" height="16" />
          <span class="text-xs px-1">{{ I18n.t("canva_cta.common.open") }}</span>
        </Badge>
      </template>
    </MenuItem>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, toRef } from "vue";
import { CanvaIcon, Badge, MenuItem, Spinner } from "prism";
import { CanvaVariant, CanvaCTAIdentifier } from "@/composables/canva_link_builder";
import { Image } from "@/modules/internal_api/image";
import IconDesign from "@/components/upload/editor_components/side_panel_icons/icon-design.vue";

import { I18n as I18nType } from "i18n-js";
import { PersistentStore } from "@/stores/persistent_store";
import { Stage } from "konva/lib/Stage";
import { useCanvaRedirect } from "@/composables/canva_redirect";

export interface CanvaCtaNewProps {
  image: Image;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
  stage: any; // Stage ref component with getStage() method
  stageHd: () => Promise<Stage>;
  getDataURL: (image: Image, stage: any, element?: any) => string;
  canvaVariant: CanvaVariant;
  canvaCtaIdentifier: CanvaCTAIdentifier;
  label: string;
  icon?: any;
}
const props = withDefaults(defineProps<CanvaCtaNewProps>(), {
  canvaVariant: CanvaVariant.INLINE_EDITOR_DEFAULT,
  icon: IconDesign,
});

const I18n: I18nType = inject("I18n");

const iconComponent = computed(() => {
  return props.icon || IconDesign;
});

const { isLoading, redirectToCanva } = useCanvaRedirect({
  image: toRef(props, "image"),
  persistentStore: props.persistentStore,
  refreshAllHdLayers: props.refreshAllHdLayers,
  stage: toRef(props, "stage"),
  stageHd: props.stageHd,
  getDataURL: props.getDataURL,
  canvaVariant: props.canvaVariant,
  canvaCtaIdentifier: props.canvaCtaIdentifier,
});

const shouldShowLoading = computed(() => isLoading.value);
</script>

<style scoped>
.truncate {
  max-width: 150px !important;
}

.loading-animation-container {
  scale: 0.6;
}
</style>
